import React, { useEffect, useState } from 'react';
import { 
    Box, 
    TextField, 
    Typography, 
    Paper, 
    Divider, 
    Button, 
    Switch,
    Checkbox,
    FormHelperText,
    FormControlLabel,
    Alert
} from '@mui/material';
import axiosInstance from '../../../utils/axios';

const CustomButtonEditor = () => {
    const [settings, setSettings] = useState({
        custom_button: {
            only_with_purchase: false,
            enabled: false,
            button_name: '📌 Информация',
            button_text: 'Здесь будет ваш текст'
        }
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const handleOnlyWithPurchaseChange = (event) => {
        setSettings({
            ...settings,
            custom_button: {
                ...settings.custom_button,
                only_with_purchase: event.target.checked
            }
        });
    };

    // Загружаем настройки при монтировании компонента
    const fetchSettings = async () => {
        try {
            const response = await axiosInstance.get('/bots-settings/get');
            if (response.data) {
                setSettings({
                    custom_button: {
                        ...(response.data.custom_button || {
                            enabled: false,
                            button_name: '📌 Информация',
                            button_text: 'Здесь будет ваш текст'
                        }),
                        only_with_purchase: response.data.custom_button?.only_with_purchase === true
                    }
                });
            }
        } catch (error) {
            console.error('Ошибка загрузки настроек:', error);
            setMessage({ type: 'error', text: 'Ошибка загрузки настроек' });
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    // Обработчик изменения включения/отключения
    const handleToggle = (event) => {
        setSettings({
            ...settings,
            custom_button: {
                ...settings.custom_button,
                enabled: event.target.checked
            }
        });
    };

    // Обработчик изменения названия кнопки
    const handleButtonNameChange = (event) => {
        setSettings({
            ...settings,
            custom_button: {
                ...settings.custom_button,
                button_name: event.target.value
            }
        });
    };

    // Обработчик изменения текста
    const handleButtonTextChange = (event) => {
        setSettings({
            ...settings,
            custom_button: {
                ...settings.custom_button,
                button_text: event.target.value
            }
        });
    };

    // Сохранение настроек
    const handleSave = async () => {
        try {
            setLoading(true);
            setMessage({ type: '', text: '' });

            const response = await axiosInstance.post('/bots-settings/update', settings);
            
            if (response.data.status) {
                setMessage({ type: 'success', text: 'Настройки успешно обновлены! Боты перезапущены.' });
            } else {
                setMessage({ type: 'error', text: 'Ошибка при обновлении настроек' });
            }
        } catch (error) {
            console.error('Ошибка сохранения:', error);
            setMessage({ type: 'error', text: 'Ошибка при сохранении настроек' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 3, bgcolor: 'background.paper' }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#333' }}>
                Настройка кастомной кнопки
            </Typography>

            <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
                Добавьте дополнительную кнопку в главное меню бота рядом с "Активные заявки"
            </Typography>

            <Divider sx={{ my: 2 }} />

            {message.text && (
                <Alert severity={message.type} sx={{ mb: 2 }}>
                    {message.text}
                </Alert>
            )}

            {/* Переключатель включения/отключения */}
            <Box sx={{ mb: 3 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={settings.custom_button.enabled}
                            onChange={handleToggle}
                            color="primary"
                        />
                    }
                    label={
                        <Typography sx={{ fontWeight: 'bold', color: '#00796b' }}>
                            {settings.custom_button.enabled ? 'Кнопка включена' : 'Кнопка отключена'}
                        </Typography>
                    }
                />
            </Box>

            <Box sx={{ mb: 3 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={settings.custom_button.only_with_purchase}
                            onChange={handleOnlyWithPurchaseChange}
                            disabled={!settings.custom_button.enabled}
                            slotProps={{ input: { 'aria-describedby': 'only-with-purchase-helper' } }}
                        />
                    }
                    label="Показывать кнопку только у пользователей с покупкой"
                />
                <FormHelperText id="only-with-purchase-helper">
                    Учитываются оплаченные заказы товаров, в том числе покупки с баланса. Пополнения баланса не учитываются.
                </FormHelperText>
            </Box>

            {/* Поле ввода для названия кнопки */}
            <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontWeight: 'bold', color: '#00796b', mb: 1 }}>
                    Название кнопки
                </Typography>
                <TextField
                    variant="outlined"
                    fullWidth
                    value={settings.custom_button.button_name}
                    onChange={handleButtonNameChange}
                    disabled={!settings.custom_button.enabled}
                    placeholder="📌 Информация"
                    helperText="Это название будет отображаться на кнопке в боте"
                    sx={{
                        bgcolor: 'background.default',
                        borderRadius: 1,
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: '#00796b',
                            },
                            '&:hover fieldset': {
                                borderColor: '#004d40',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#004d40',
                            },
                        },
                    }}
                />
            </Box>

            {/* Поле ввода для текста кнопки */}
            <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontWeight: 'bold', color: '#00796b', mb: 1 }}>
                    Текст при нажатии на кнопку
                </Typography>
                <TextField
                    multiline
                    rows={6}
                    variant="outlined"
                    fullWidth
                    value={settings.custom_button.button_text}
                    onChange={handleButtonTextChange}
                    disabled={!settings.custom_button.enabled}
                    placeholder="Здесь будет ваш текст..."
                    helperText="Этот текст будет отправлен пользователю при нажатии на кнопку"
                    sx={{
                        bgcolor: 'background.default',
                        borderRadius: 1,
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: '#00796b',
                            },
                            '&:hover fieldset': {
                                borderColor: '#004d40',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#004d40',
                            },
                        },
                    }}
                />
            </Box>

            {/* Кнопка сохранения */}
            <Button 
                variant="contained" 
                color="primary" 
                onClick={handleSave}
                disabled={loading}
                sx={{ mt: 2 }}
            >
                {loading ? 'Сохранение...' : 'Сохранить настройки'}
            </Button>
        </Paper>
    );
};

export default CustomButtonEditor;
